import { z } from "zod";
import {
  IdSchema,
  IsoDateOrDateTimeSchema,
  IsoDateTimeSchema,
  QueryBooleanSchema,
} from "./common.js";
import { SWAP_SOURCES } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §6, harness/SWAP-POLICY.md ★
 */

export const SerialSwapSchema = z
  .object({
    id: IdSchema,
    case_id: IdSchema,
    original_serial: z.string().min(1),
    replacement_serial: z.string().min(1),
    secui_case_no: z.string().nullable(),
    // 교체일은 수집 원문에서 날짜로만 오는 경우가 많다. 모르면 null 이다 (C14 와 같은 규칙)
    swapped_at: IsoDateOrDateTimeSchema.nullable(),
    source: z.enum(SWAP_SOURCES),
    confidence: z.number().min(0).max(1),
    confirmed_by: IdSchema.nullable(),
    confirmed_at: IsoDateTimeSchema.nullable(),
    evidence_ref: z.string().min(1), // harness/SWAP-POLICY.md §6 ⑦ — 항상 존재해야 한다
  })
  .refine((v) => v.original_serial !== v.replacement_serial, {
    message: "자기 자신 매칭 금지 (harness/SWAP-POLICY.md §3)",
  });
export type SerialSwap = z.infer<typeof SerialSwapSchema>;

export const SwapListQuerySchema = z.object({
  serial: z.string().optional(),
  /** 사람 확인 대기 큐 — confidence < 1.0 이고 아직 확인되지 않은 것 */
  pending_only: QueryBooleanSchema.optional(),
});
export type SwapListQuery = z.infer<typeof SwapListQuerySchema>;

export const SwapListResponseSchema = z.object({
  items: z.array(SerialSwapSchema),
});
export type SwapListResponse = z.infer<typeof SwapListResponseSchema>;

/**
 * 확인. **`confirmed_by` 를 본문으로 받지 않는다** (2026-09-09 결정 C25).
 * 누가 확인했는지는 세션에서 온다 — 본문으로 받으면 남의 이름으로 확인을 찍을 수
 * 있고, 그러면 "사람이 확인했다"는 기록의 의미가 없어진다(SOUL S5).
 */
export const SwapConfirmRequestSchema = z.object({
  note: z.string().trim().min(1).optional(),
});
export type SwapConfirmRequest = z.infer<typeof SwapConfirmRequestSchema>;

/**
 * ★ 수기 교체 등록 (harness/SWAP-POLICY.md §2-1).
 *
 * 수집기가 생기기 전까지 원 시리얼 ↔ 교체 시리얼을 이을 **유일한 경로**다.
 * `original_serial` 을 본문으로 받지 않는다 — 케이스의 시리얼에서만 온다.
 * 본문으로 받으면 자기 케이스가 아닌 시리얼에 교체품을 붙일 수 있고,
 * 그러면 시리얼 축이 조용히 어긋난다.
 */
export const SwapCreateRequestSchema = z.object({
  case_id: IdSchema,
  replacement_serial: z.string().trim().min(1),
  swapped_at: IsoDateOrDateTimeSchema.nullable().optional(),
  /**
   * 근거 없이는 남지 않는다 (SWAP-POLICY §4). 수기 매칭에서는 이 문장 자체가 근거다.
   *
   * SECUI 접수번호도 **여기에** 적는다. `secui_case_no` 열은 외래키라
   * "그 케이스 원문을 실제로 가져왔다"는 뜻이고, 수기 등록 시점에는 그게 아직 없다.
   * 없는 것을 있는 것처럼 적으면 그 열의 의미가 사라진다.
   */
  evidence_ref: z.string().trim().min(2),
});
export type SwapCreateRequest = z.infer<typeof SwapCreateRequestSchema>;

/** 잘못 적은 교체를 지운다. 사유 없이 지우면 나중에 아무것도 설명하지 못한다 */
export const SwapDeleteRequestSchema = z.object({
  reason: z.string().trim().min(2),
});
export type SwapDeleteRequest = z.infer<typeof SwapDeleteRequestSchema>;
